import { ConnectionStates, avSelectors, useAvContext } from "@minervaproject/avsubsystem";
import { useCallback, useEffect } from "react";

import { createErrorToast } from "components/shared/toasts";

// This observer is used to retry autoplay for all streams when a user interacts with the page after
// an autoplay error is detected.
function AutoplayObserver() {
  const avConnection = useAvContext();
  const connectionReady = avSelectors.useConnectionState() === ConnectionStates.connected;
  const hasAutoplayError = avSelectors.useHasAnyStreamAutoplayError();

  const retryAutoplay = useCallback(() => {
    window.flightLogger.log("[AutoplayObserver] Retrying autoplay for all streams");
    avConnection?.retryAutoplayForAllStreams();
  }, [avConnection]);

  useEffect(() => {
    if (!hasAutoplayError || !connectionReady) {
      return;
    }

    createErrorToast(
      "Autoplay issue: participant audio is not playing",
      "Please interact with the page to enable audio",
    );

    const ac = new AbortController();
    const { signal } = ac;
    let didRetry = false;

    const onUserGesture = () => {
      if (didRetry) {
        return;
      }
      didRetry = true;
      retryAutoplay();
      ac.abort();
    };

    const opts = { capture: true, signal } as const;
    window.addEventListener("pointerdown", onUserGesture, opts);
    window.addEventListener("keydown", onUserGesture, opts);

    return () => ac.abort();
  }, [hasAutoplayError, connectionReady, retryAutoplay]);

  return null;
}

export { AutoplayObserver };
